import * as React from 'react'
import { Download, Copy, ChevronDown, Table, Code } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { ExportReport } from '@/types'
import { generateJSONReport, generateLaTeXTable, generateHTMLReport, downloadFile } from '@/lib/reportGenerator'
import { generateFullCSV, generatePythonScript } from '@/lib/reportGenerator'

interface ExportButtonProps {
  report: ExportReport
  className?: string
}

export function ExportButton({ report, className }: ExportButtonProps) {
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleDownloadJSON = () => {
    const content = generateJSONReport(report)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    downloadFile(content, `bitassay-report-${timestamp}.json`, 'application/json')
    setOpen(false)
  }

  const handleDownloadLaTeX = () => {
    const content = generateLaTeXTable(report)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    downloadFile(content, `bitassay-table-${timestamp}.tex`, 'text/x-latex')
    setOpen(false)
  }

  const handleDownloadCSV = () => {
    const content = generateFullCSV(report)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    downloadFile(content, `bitassay-report-${timestamp}.csv`, 'text/csv')
    setOpen(false)
  }

  const handleDownloadPython = () => {
    const content = generatePythonScript(report)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    downloadFile(content, `bitassay-analysis-${timestamp}.py`, 'text/x-python')
    setOpen(false)
  }

  const handleDownloadReport = () => {
    const content = generateHTMLReport(report)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
    downloadFile(content, `bitassay-compliance-report-${timestamp}.html`, 'text/html')
    setOpen(false)
  }

  const handleCopyLaTeX = async () => {
    const content = generateLaTeXTable(report)
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    setOpen(false)
  }

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        aria-label="Export results"
        aria-expanded={open}
      >
        <Download className="h-4 w-4" />
        Export
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </Button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-48 rounded-md border bg-popover p-1 shadow-md">
          <button
            type="button"
            onClick={handleDownloadJSON}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-4 w-4" />
            Download JSON
          </button>
          <button
            type="button"
            onClick={handleDownloadCSV}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Table className="h-4 w-4" />
            Download CSV
          </button>
          <button
            type="button"
            onClick={handleDownloadLaTeX}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-4 w-4" />
            Download LaTeX
          </button>
          <button
            type="button"
            onClick={handleDownloadPython}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Code className="h-4 w-4" />
            Python Script
          </button>
          <button
            type="button"
            onClick={handleDownloadReport}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-4 w-4" />
            Compliance Report (HTML)
          </button>
          <div className="my-0.5 h-px bg-border" />
          <button
            type="button"
            onClick={() => void handleCopyLaTeX()}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copied!' : 'Copy LaTeX'}
          </button>
        </div>
      )}
    </div>
  )
}
